import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectBox, Panel } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

function MarketFields({ market, onSaved }) {
  const [name, setName] = useState(market.name);
  const [fields, setFields] = useState(market.fields);
  useEffect(() => { setName(market.name); setFields(market.fields); }, [market]);
  const upd = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const save = async () => {
    try {
      await api.put(`/markets/${market.key}`, { name, fields });
      toast.success(`${name} fields saved`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title={market.name} testid={`fields-panel-${market.key}`} action={<Button size="sm" onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid={`save-fields-${market.key}`}>Save</Button>}>
      <div className="space-y-1.5 mb-4"><span className="eyebrow">Market display name</span><Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`market-name-${market.key}`} /></div>
      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={f.key || i} className={`grid grid-cols-[1fr_110px] sm:grid-cols-[1fr_110px_1fr_auto] gap-2 items-center ${f.active ? "" : "opacity-50"}`}>
            <Input value={f.label} onChange={(e) => upd(i, { label: e.target.value })} data-testid={`field-label-${market.key}-${i}`} />
            <SelectBox value={f.type} onChange={(v) => upd(i, { type: v || "text" })} testid={`field-type-${market.key}-${i}`} className="w-full" options={["text", "number", "date", "select"].map((t) => ({ value: t, label: t }))} />
            <Input placeholder={f.type === "select" ? "Options, comma separated" : "—"} disabled={f.type !== "select"} value={f.options.join(", ")} onChange={(e) => upd(i, { options: e.target.value.split(",") })} />
            <Switch checked={f.active} onCheckedChange={(v) => upd(i, { active: v })} data-testid={`field-active-${market.key}-${i}`} />
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => setFields([...fields, { key: "", label: "New field", type: "text", options: [], active: true }])} data-testid={`add-field-${market.key}`}><Plus className="h-4 w-4 mr-1" />Add field</Button>
      <p className="text-xs text-slate-400 mt-2">Fields can be deactivated but not deleted, so existing lead data is never lost.</p>
    </Panel>
  );
}

export default function FieldsTab() {
  const { meta, refreshMeta } = useAuth();
  return (
    <div className="grid xl:grid-cols-2 gap-4">
      <Panel title="Core fields (all markets)">
        <div className="flex flex-wrap gap-2">{meta.core_fields.map((f) => <span key={f.key} className="text-xs bg-slate-100 rounded px-2 py-1">{f.label}</span>)}</div>
        <p className="text-xs text-slate-400 mt-2">Core fields power search, duplicate detection and reports. Contact name is always required.</p>
      </Panel>
      {meta.markets.map((m) => <MarketFields key={m.key} market={m} onSaved={refreshMeta} />)}
    </div>
  );
}
